import React, { useState, useEffect } from "react";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// SUBEB Recruitment CBT follows the NCS full-flow but with SUBEB-specific rules and sections

const Subeb = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  const subebExamMeta = {
    subject: "States Universal Basic Education Board (SUBEB) Recruitment CBT",
    timeAlloted: 2700, // 45 minutes — canonical SUBEB duration
    description: `This mock simulates a SUBEB Recruitment CBT. Sections include English Language, Mathematics / Quantitative Reasoning, Teaching Methodology (Pedagogy), General Knowledge / Current Affairs, and Specialized Subject Questions where applicable to the candidate's teaching specialization.\n\nConduct Rules:\n- Centre or remote CBT depending on state; adhere to single-window policy and time limits.\n- Use a laptop/desktop where possible; avoid switching windows.\n- Keep answers to provided options; no external resources allowed.\n\nBlueprint (approximation):\n- 50 MCQs across the sections listed above.\n- Questions sourced only from verified SUBEB past questions where available.\n\nDisclaimer: This mock is an informed approximation for practice and training; SUBEBs across states may vary in blueprint and specialization requirements.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: subebExamMeta.subject,
      timeAlloted: subebExamMeta.timeAlloted,
      description: subebExamMeta.description,
      examName: "SUBEB",
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (error) {
      setLoading(false);
      errorSetter(error?.response?.data?.message || error?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (error?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="SUBEB MOCK"
        />

        {/* No selection screen; we jump straight to Rules */}
      </div>

      {!examReady && (
        <Rules
          examName={"States Universal Basic Education Board Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={subebExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="SUBEB"
        />
      )}
    </div>
  );
};

export default Subeb;

